/**
 * How to connect an AI assistant: the MCP URL to paste, then sign-in and
 * consent.
 */
import { useEffect, useState } from "react";
import { track } from "../../lib/analytics.ts";
import { MCP_URL } from "../../lib/assistants.ts";
import { Button } from "../ui/button.tsx";

/** The connector URL with a Copy button; also on the in-app assistant page. */
export function CopyUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <div className="mt-2 flex items-stretch border border-divider bg-surface">
      <code className="min-w-0 flex-1 select-all truncate px-3 py-2.5 font-sans text-[16px] leading-6">
        {url}
      </code>
      <Button
        variant="ghost"
        className="shrink-0 rounded-none border-l border-divider px-3.5 text-[15px]"
        onClick={() => {
          // Clipboard access can be refused; the URL stays selectable either way.
          navigator.clipboard?.writeText(url).then(
            () => {
              setCopied(true);
              track("mcp url copied");
            },
            () => {},
          );
        }}
      >
        <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
      </Button>
    </div>
  );
}

export function ConnectSteps() {
  return (
    <ol className="mt-4 grid gap-x-8 gap-y-5 md:grid-cols-3">
      <li className="min-w-0 border-t border-ink pt-3">
        <div className="kicker">Step 1</div>
        <h4 className="heading mt-1 text-[19px]">Add Babia to your assistant</h4>
        <p className="mt-1 text-[15px]">
          Add a custom connector with this URL. ChatGPT calls it an app; others, a remote MCP
          server.
        </p>
        <CopyUrl url={MCP_URL} />
      </li>
      <li className="min-w-0 border-t border-ink pt-3">
        <div className="kicker">Step 2</div>
        <h4 className="heading mt-1 text-[19px]">Sign in and allow it</h4>
        <p className="mt-1 text-[15px]">
          Your assistant opens Babia. Sign in with the Google or Facebook account you use for the
          app, then allow access on the next screen. Your assistant can then read and write your
          household’s log.
        </p>
      </li>
      <li className="min-w-0 border-t border-ink pt-3">
        <div className="kicker">Step 3</div>
        <h4 className="heading mt-1 text-[19px]">Say what happened</h4>
        <p className="mt-1 text-[15px]">
          “Both had 3 oz twenty minutes ago.” The entries land on both phones within seconds, the
          same as a tap.
        </p>
      </li>
    </ol>
  );
}
